import React, { useMemo } from 'react';

/**
 * ImpactSummary - Displays impact metrics based on cart contents and selected mode
 * 
 * Shows environmental, community, or cost impact of sharing props.
 * Updates instantly when impactMode changes.
 * Layout: Horizontal on mobile, vertical on desktop
 * 
 * @param {Array} selectedItems - Items currently in cart
 * @param {string|null} impactMode - Active impact mode ('environmental' | 'community' | 'cost' | null)
 * @param {number} totalSelectedCount - Total number of items selected
 * @param {function} onPromptClick - Callback to open impact mode menu
 * @param {string} variant - Display variant: 'card' (default) or 'inline' (no wrapper)
 */
const ImpactSummary = ({ 
  selectedItems = [], 
  impactMode = null,
  totalSelectedCount = 0,
  onPromptClick = null,
  variant = 'card'
}) => {
  // ============================================================================
  // Impact Calculations (Demo-Ready Fake Numbers)
  // ============================================================================
  
  const impactData = useMemo(() => {
    const itemCount = totalSelectedCount;
    
    // Environmental impact (scales with item count)
    const carbonSaved = Math.round(itemCount * 12.7); // ~13 lbs CO₂ per item
    const milesEquivalent = Math.round(carbonSaved * 1.22); // 1 lb CO₂ ≈ 1.22 miles driven
    const treesEquivalent = (carbonSaved / 48).toFixed(1); // 1 tree absorbs ~48 lbs CO₂/year
    
    // Community impact (count unique organizations)
    const uniqueOrgs = new Set(selectedItems.map(item => item.orgId)).size;
    const connectionsFormed = uniqueOrgs > 1 ? uniqueOrgs * (uniqueOrgs - 1) / 2 : 0; // n(n-1)/2 connections
    
    // Cost impact (scales with item count)
    const moneySaved = itemCount * 85; // ~$85 saved per shared item
    const purchaseCostAvoided = itemCount * 340; // ~$340 purchase cost per item
    const percentSaved = itemCount > 0 ? Math.round((moneySaved / purchaseCostAvoided) * 100) : 0;
    
    return {
      environmental: {
        primary: carbonSaved,
        primaryUnit: 'lbs',
        primaryLabel: 'CO₂ saved',
        secondary: `≈ driving ${milesEquivalent} miles`,
        tertiary: `≈ ${treesEquivalent} trees for 1 year`,
        icon: '🌱',
        color: 'from-green-500 to-emerald-600'
      },
      community: {
        primary: uniqueOrgs,
        primaryUnit: uniqueOrgs === 1 ? 'organization' : 'organizations',
        primaryLabel: 'supported',
        secondary: connectionsFormed > 0 
          ? `${connectionsFormed} ${connectionsFormed === 1 ? 'connection' : 'connections'} formed`
          : 'Start selecting to connect',
        tertiary: itemCount > 0 ? `${itemCount} props shared across community` : null,
        icon: '🤝',
        color: 'from-blue-500 to-indigo-600'
      },
      cost: {
        primary: moneySaved,
        primaryUnit: '',
        primaryLabel: 'saved',
        secondary: `vs. $${purchaseCostAvoided.toLocaleString()} to purchase`,
        tertiary: percentSaved > 0 ? `${percentSaved}% cost reduction` : null,
        icon: '💰',
        color: 'from-yellow-500 to-amber-600'
      }
    };
  }, [selectedItems, totalSelectedCount]);

  // Get glow color based on impact mode
  const getGlowColor = (mode) => {
    switch(mode) {
      case 'environmental': return 'rgba(34, 197, 94, 0.5)'; // green-500 with opacity
      case 'community': return 'rgba(59, 130, 246, 0.5)'; // blue-500 with opacity
      case 'cost': return 'rgba(234, 179, 8, 0.5)'; // yellow-500 with opacity
      default: return 'rgba(59, 130, 246, 0.5)';
    }
  };

  // Don't render if no items
  if (totalSelectedCount === 0) {
    return null;
  }

  // Show prompt when items exist but no impact mode selected
  if (!impactMode) {
    return (
      <div className="flex items-center justify-center pointer-events-none">
        <button
          onClick={onPromptClick}
          disabled={!onPromptClick}
          className="relative z-10 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg shadow-lg px-4 py-1.5 w-full max-w-md border-2 border-dashed border-gray-300 pointer-events-auto transition-all duration-300 lg:hover:shadow-2xl lg:hover:shadow-blue-300/50 lg:hover:scale-105 disabled:cursor-default disabled:hover:scale-100 cursor-pointer active:scale-95"
        >
          {/* Horizontal layout */}
          <div className="flex items-center gap-3">
            {/* Prompt */}
            <div className="flex-1 text-left">
              <div className="text-xs font-semibold">
                See Your Impact ↗
              </div>
              <div className="text-[10px] opacity-75">
                Select an Impact Mode
              </div>
            </div>

            {/* Visual cue */}
            <div className="flex justify-center gap-1 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
            </div>
          </div>
        </button>
      </div>
    );
  }

  const impact = impactData[impactMode];
  if (!impact) return null;

  // Format primary number (add $ for cost, add commas for large numbers)
  const formattedPrimary = impactMode === 'cost' 
    ? `${impact.primary.toLocaleString()}`
    : impact.primary.toLocaleString();

  // If inline variant, return content without card wrapper
  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-3 text-white">
        {/* Left side: Icon + Primary Metric inline */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Icon */}
          <div className="text-2xl">
            {impact.icon}
          </div>

          {/* Primary Metric */}
          <div className="text-center">
            <div className="text-2xl font-bold leading-none">
              {formattedPrimary}
            </div>
            {impact.primaryUnit && (
              <div className="text-[10px] opacity-90 font-medium whitespace-nowrap">
                {impact.primaryUnit}
              </div>
            )}
          </div>
        </div>

        {/* Right side: Label + Details */}
        <div className="flex-1 flex flex-col justify-center min-w-0">
          {/* Label */}
          <div className="text-left text-xs font-semibold mb-0.5 opacity-95">
            {impact.primaryLabel}
          </div>

          {/* Secondary Detail */}
          <div className="text-left text-[10px] opacity-80 border-t border-white/20 pt-0.5">
            {impact.secondary}
          </div>
        </div>
      </div>
    );
  }

  // Default card variant

  return (
    <div className="flex items-center justify-center pointer-events-none">
      <div 
        className={`
          bg-gradient-to-r ${impact.color} 
          text-white rounded-lg shadow-md
          px-4 py-1.5 w-full max-w-md
          transform transition-all duration-300
          hover:shadow-lg hover:scale-105
          pointer-events-auto
        `}
      >
        {/* Horizontal layout */}
        <div className="flex items-center gap-3">
          {/* Left side: Icon + Primary Metric inline */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {/* Icon */}
            <div className="text-2xl animate-pulse">
              {impact.icon}
            </div>

            {/* Primary Metric */}
            <div className="text-center">
              <div className="text-2xl font-bold leading-none">
                {formattedPrimary}
              </div>
              {impact.primaryUnit && (
                <div className="text-[10px] opacity-90 font-medium whitespace-nowrap">
                  {impact.primaryUnit}
                </div>
              )}
            </div>
          </div>

          {/* Right side: Label + Details */}
          <div className="flex-1 flex flex-col justify-center min-w-0">
            {/* Label */}
            <div className="text-left text-xs font-semibold mb-0.5 opacity-95">
              {impact.primaryLabel}
            </div>

            {/* Secondary Detail */}
            <div className="text-left text-[10px] opacity-80 border-t border-white/20 pt-0.5">
              {impact.secondary}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactSummary;
